import React from 'react';
import { LegalModel } from './useLegalEditor';
import { AdminModal } from '../../pages/admin/ConfirmationDialogs';

const LegalPublishModal: React.FC<{ model: LegalModel }> = ({ model }) => {
  const {
    showPublishModal, setShowPublishModal, publishVersion, setPublishVersion,
    publishSummary, setPublishSummary, publishRequiresReconsent,
    setPublishRequiresReconsent, consentStats, publishing, handlePublish,
  } = model;

  if (!showPublishModal) return null;

  return (
    <AdminModal ariaLabel="Publish new version" onCancel={() => setShowPublishModal(false)} maxWidth="32rem">
        <h3 className="text-lg font-bold mb-4" style={{ color: 'var(--rp-text)' }}>
          Publish New Version
        </h3>
        <p className="text-sm mb-4" style={{ color: 'var(--rp-text-secondary)' }}>
          Publishing creates a new version record. The content currently in the editor will become the live version
          on both the website and in the iOS app.
        </p>

        <div className="space-y-4 mb-6">
          <div>
            <label htmlFor="legal-publish-version" className="block text-xs font-semibold mb-1" style={{ color: 'var(--rp-text-secondary)' }}>
              Version Number
            </label>
            <input
              id="legal-publish-version"
              type="text"
              value={publishVersion}
              onChange={e => setPublishVersion(e.target.value)}
              placeholder="e.g., 1.1"
              className="w-full px-3 py-2 rounded-lg border bg-transparent text-sm"
              style={{ borderColor: 'var(--rp-border)', color: 'var(--rp-text)' }}
            />
          </div>
          <div>
            <label htmlFor="legal-publish-summary" className="block text-xs font-semibold mb-1" style={{ color: 'var(--rp-text-secondary)' }}>
              Change Summary (optional)
            </label>
            <textarea
              id="legal-publish-summary"
              value={publishSummary}
              onChange={e => setPublishSummary(e.target.value)}
              rows={3}
              placeholder="Describe what changed in this version..."
              className="w-full px-3 py-2 rounded-lg border bg-transparent text-sm"
              style={{ borderColor: 'var(--rp-border)', color: 'var(--rp-text)' }}
            />
          </div>
          <div className="p-4 rounded-lg border" style={{
            borderColor: publishRequiresReconsent ? 'var(--admin-warning)' : 'var(--rp-border)',
            backgroundColor: publishRequiresReconsent ? 'color-mix(in srgb, var(--admin-warning) 10%, transparent)' : 'rgba(255,255,255,0.02)'
          }}>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={publishRequiresReconsent}
                onChange={e => setPublishRequiresReconsent(e.target.checked)}
                className="mt-1"
              />
              <div>
                <span className="text-sm font-semibold block" style={{ color: publishRequiresReconsent ? 'var(--admin-warning)' : 'var(--rp-text)' }}>
                  Material Change — Require Re-consent
                </span>
                <span className="text-xs block mt-1" style={{ color: 'var(--rp-text-secondary)' }}>
                  Check this if the update includes new data collection, changed liability terms, or other material changes.
                  All existing users will be prompted to review and accept the updated document on their next app launch.
                  Required by Apple and GDPR for significant changes.
                </span>
                {publishRequiresReconsent && consentStats && (
                  <span className="text-xs block mt-2 font-semibold" style={{ color: 'var(--admin-warning)' }}>
                    Warning: This will prompt {consentStats.consented_current} users to re-accept on next launch.
                    Users who decline will be signed out.
                  </span>
                )}
              </div>
            </label>
          </div>
        </div>

      <div className="flex gap-3 justify-end">
        <button
          onClick={() => setShowPublishModal(false)}
          className="px-4 py-2 rounded-lg text-sm"
          style={{ color: 'var(--rp-text-secondary)' }}
        >
          Cancel
        </button>
        <button
          onClick={handlePublish}
          disabled={publishing || !publishVersion}
          className="px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all disabled:opacity-50"
          style={{ backgroundColor: publishRequiresReconsent ? 'var(--admin-warning-strong)' : 'var(--admin-primary)' }}
        >
          {publishing ? 'Publishing...' : publishRequiresReconsent ? 'Publish (Material Change)' : 'Publish Update'}
        </button>
      </div>
    </AdminModal>
  );
};

export default LegalPublishModal;
